import clsx from "clsx";
import Icon from "../Icon/Icon";
import React, { createContext, HTMLProps, useContext } from "react";
import type { NodeApi, NodeRendererProps } from "react-arborist";
import { createPluggable } from "pluggable-react";
import { TreeContext, type TreeNodeData } from "./Tree";
import TreeLine from "./TreeLine";
import styles from "./Tree.module.scss";
import { composeRefs } from "@radix-ui/react-compose-refs";
import Button from "../Button/Button";
import { withRefProp } from "../../../utils/withRefProp";

type TreeItemContextType = {
  node: TreeNode<any>;
  isActive: boolean;
  onSelect: (id: string) => void;
  style?: React.CSSProperties;
  dragHandle?: any;
  focusSelected?: boolean;
};

const TreeItemContext = createContext<TreeItemContextType | null>(null);

export type TreeNode<TInfo = unknown> = NodeApi<TreeNodeData<TInfo>>;

export interface TreeItemProps<TInfo = unknown> extends NodeRendererProps<TreeNodeData<TInfo>>, Omit<HTMLProps<"div">, "onSelect" | "style"> {
  isActive: boolean;
  onSelect: (id: string) => void;
  dragHandle?: any;
  indent?: number;
  actionsOpen?: boolean;
  focusSelected?: boolean;
}

const DefaultTreeItem = withRefProp(<TInfo = unknown>(props: TreeItemProps<TInfo>) => {
  const {
    node, isActive, onSelect, style, ref, dragHandle, children, className, onClick, actionsOpen,
    focusSelected = true,
    ...otherProps
  } = props;
  const treeContext = useContext(TreeContext);
  const level = node.level;
  const indentSize = Number.parseFloat(`${style.paddingLeft || 0}`);
  const showIndentBorders = treeContext?.showIndentBorders ?? true;

  const buildLevelsInfo = () => {
    const pathReversed: TreeNode[] = [];
    let cursor: TreeNode<any> | null = node as any;
    while (cursor) {
      pathReversed.push(cursor);
      cursor = cursor.parent ?? null;
    }
    const info: Array<{
      index: number;
      showTopLine: boolean;
      showBottomLine: boolean;
      showRightLine: boolean;
    }> = [];
    for (let i = 0; i < level; i++) {
      const depthFromCurrent = level - 1 - i;
      const target = pathReversed[depthFromCurrent];
      const isCurrentLevel = i === level - 1;
      if (!target) {
        info.push({ index: i, showTopLine: false, showBottomLine: false, showRightLine: isCurrentLevel });
        continue;
      }
      const parent = target.parent as any;
      if (!parent?.children) {
        info.push({ index: i, showTopLine: false, showBottomLine: false, showRightLine: isCurrentLevel });
        continue;
      }
      const isLast = target.childIndex >= parent.children.length - 1;
      info.push({
        index: i,
        showTopLine: isCurrentLevel || !isLast,
        showBottomLine: !isCurrentLevel && !isLast ? true : !isLast,
        showRightLine: isCurrentLevel,
      });
    }
    return info;
  };

  const levelsInfo = buildLevelsInfo();
  const selectable = focusSelected && node.data.selectable !== false;

  return (
    <TreeItemContext.Provider value={{ node, isActive, onSelect, style, dragHandle, focusSelected }}>
      <div
        style={{ ...style, display: "flex", alignItems: "center", position: "relative" }}
        ref={composeRefs(dragHandle, ref)}
        role="treeitem"
        className={clsx(styles.treeItem, className)}
        {...otherProps as any}
        data-deci-tree-item="true"
        data-selectable={selectable ? "true" : "false"}
        data-active={isActive ? "true" : "false"}
        data-actions-open={actionsOpen ? "true" : "false"}
        onClick={(e) => {
          selectable && onSelect(String(node.id));
          onClick?.(e as any);
        }}
      >
        {showIndentBorders ? (
          <div className={styles.indent} style={{ ["--indent-size" as any]: `${level > 0 ? indentSize / level : 0}px` }}>
            {levelsInfo.map((meta, idx) => (
              <TreeLine
                key={meta.index}
                showTopLine={meta.showTopLine}
                showBottomLine={meta.showBottomLine}
                showRightLine={meta.showRightLine}
              />
            ))}
          </div>
        ) : null}
        <div className={styles.content}>
          {children ?? (
            <>
              <TreeItemArrow />
              <TreeItemLabel />
            </>
          )}
        </div>
      </div>
    </TreeItemContext.Provider>
  );
});

const TreeItem = createPluggable<TreeItemProps<any>>("deci.tree.item", DefaultTreeItem);

// Arrow
export interface TreeItemArrowProps {
}

const DefaultTreeItemArrow = (_props: TreeItemArrowProps) => {
  const context = useContext(TreeItemContext);
  const treeContext = useContext(TreeContext);
  if (!context) return null;
  const { node } = context;
  const showIndentBorders = treeContext?.showIndentBorders ?? true;
  const filteredChildren = node.children ? treeContext?.filterItems(node.children) : undefined;
  const isLeaf = !filteredChildren?.length;
  const hasTreeLine = showIndentBorders && node.level > 0;
  const isControlledExpansion = treeContext?.controlledExpandedState != null;
  const controlledOpen = treeContext?.controlledExpandedState?.[String(node.id)];
  const isOpen = isControlledExpansion
    ? (typeof controlledOpen === "boolean" ? controlledOpen : Boolean(treeContext?.openByDefault))
    : node.isOpen;
  if (isLeaf) {
    return <span className={styles.spacer} />;
  }
  return (
    <Button
      label={isOpen ? "Collapse" : "Expand"}
      icon={true}
      size={"sm"}
      className={clsx(styles.arrow, "!p-0")}
      data-expanded={isOpen ? "true" : "false"}
      onClick={(e) => {
        e.stopPropagation();
        if (isControlledExpansion) {
          treeContext.toggleExpanded?.(String(node.id), isOpen);
          return;
        }
        node.toggle();
      }}
    >
      <Icon name="tree-expand" />
    </Button>
  );
};

const TreeItemArrow = createPluggable<TreeItemArrowProps>("deci.tree.item.arrow", DefaultTreeItemArrow);

// Label
export interface TreeItemLabelProps {
}

const DefaultTreeItemLabel = (_props: TreeItemLabelProps) => {
  const context = useContext(TreeItemContext);
  if (!context) return null;
  const { node } = context;
  const label = node.data?.label ?? (node.data as any)?.info?.text ?? String(node.data?.id);
  return (
    <div className={styles.labelWrap}>
      <div>{label}</div>
    </div>
  );
};

const TreeItemLabel = createPluggable<TreeItemLabelProps>("deci.tree.item.label", DefaultTreeItemLabel);

// Actions
export interface TreeItemActionsProps extends HTMLProps<"div"> {
}

const DefaultTreeItemActions = (props: TreeItemActionsProps) => {
  const { className, ...otherProps } = props;
  const context = useContext(TreeItemContext);
  if (!context) return null;
  return (
    <div
      className={clsx(styles.actions, className)}
      onClick={(e) => e.stopPropagation()}
      {...otherProps as any}
    />
  );
};

const TreeItemActions = createPluggable<TreeItemActionsProps>("deci.tree.item.actions", DefaultTreeItemActions);

export default Object.assign(TreeItem, { Arrow: TreeItemArrow, Label: TreeItemLabel, Actions: TreeItemActions });
